import React, { useRef, useEffect } from 'react';
import { Input, Modal } from 'tea-component';
import EditButton from '@/pages/strategic/components/EditButton';
import DeleteButton from '@/pages/strategic/components/DeleteButton';
import './index.scss';

interface PhaseCellProps {
  titleText: string;
  onChange: (value: any) => void;
  onDelete?: () => void; // 删除回调函数
  deleteMessage?: string; // 删除提示语
  showDelete?: { disabled: boolean; tooltip?: string }; // 是否显示删除图标，可以是 boolean 或对象（disabled 控制禁用，tooltip 提示语）
  formatMessage: (descriptor: { id: string }) => string; // 国际化函数
  isNewRow?: boolean; // 是否是新增行
  hasError?: boolean; // 是否有错误（显示红色边框）
}

// Phase单元格组件，用于处理重命名和删除逻辑
const PhaseCell: React.FC<PhaseCellProps> = ({ titleText, onChange, onDelete, deleteMessage, showDelete, formatMessage, isNewRow = false, hasError = false }) => {
  // 如果是新增行且标题为空，默认进入编辑状态
  const [isEditing, setIsEditing] = React.useState(isNewRow && !titleText);
  const [inputValue, setInputValue] = React.useState(titleText);
  const [isFocused, setIsFocused] = React.useState(false); // 输入框是否聚焦
  const inputRef = useRef<any>(null);

  // 解析 showDelete 配置
  const deleteConfig = showDelete;
  const isDeleteVisible = !!showDelete; // 如果传入了 showDelete 对象，则显示删除按钮
  const isDeleteDisabled = deleteConfig?.disabled || false;
  
  // 当titleText变化时更新inputValue
  useEffect(() => {
    setInputValue(titleText);
  }, [titleText]);

  // 进入编辑模式时自动聚焦
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // 选中所有文本
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleEditClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsEditing(true);
  };

  const handleInputFocus = () => {
    setIsFocused(true);
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    
    // 如果输入为空或只有空格，保持编辑状态
    if (!inputValue.trim()) {
      return;
    }
    
    // 失焦时确认修改
    if (inputValue !== titleText) {
      onChange(inputValue.trim());
    }
    
    setIsEditing(false);
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      // 如果输入为空或只有空格，保持编辑状态
      if (!inputValue.trim()) {
        return;
      }
      
      // 按Enter键确认
      if (inputValue !== titleText) {
        onChange(inputValue.trim());
      }
      
      setIsEditing(false);
    } else if (e.key === 'Escape') {
      // 按ESC键取消
      setInputValue(titleText);
      setIsEditing(false);
      setIsFocused(false);
    }
  };

  const handleDeleteClick = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    // 如果删除被禁用，则不执行
    if (isDeleteDisabled) {
      return;
    }
    
    // 如果 titleText 为空，直接删除不需要确认
    if (!inputValue || inputValue.trim() === '') {
      if (onDelete) {
        onDelete();
      }
      return;
    }
    // 显示确认弹窗
    const confirmed = await Modal.confirm({
      className: 'dbs-delete-confirm-modal',
      message: deleteMessage,
      okText: formatMessage({ id: 'dbs_confirm' }),
      cancelText: formatMessage({ id: 'dbs_cancel' }),
      size: 'auto'
    });
    if (confirmed) {
      if (onDelete) {
        onDelete();
      }
    }
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleInputMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // 判断是否为All Total，如果是则不显示编辑和删除按钮
  const isAllTotal = titleText === 'All Total';
  
  
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' }}>
      {isEditing ? (
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          onClick={handleInputClick}
          onMouseDown={handleInputMouseDown}
          placeholder='Please enter' 
          style={{ 
            width: '200px',
            height: '28px',
            ...(hasError && !isFocused ? {
              border: '1px solid #EF5858',
              boxShadow: '0 0 0 2px rgba(239, 88, 88, 0.2)'
            } : {})
          }}
        />
      ) : (
        <>
          <span>{titleText || ''}</span>
          {!isAllTotal && (
            <EditButton
              onClick={handleEditClick}
              title={formatMessage({ id: 'dbs_rename' })}
              useBubble={true}
              className="dbs-phase-cell-edit-btn"
            />
          )}
        </>
      )}
      {!isAllTotal && isDeleteVisible && (
        <DeleteButton
          onClick={handleDeleteClick}
          title={formatMessage({ id: 'dbs_delete' })}
          disabled={isDeleteDisabled}
          disabledTooltip={deleteConfig?.tooltip || undefined}
          className="dbs-phase-cell-delete-btn"
        />
      )}
    </div>
  );
};

export default PhaseCell;
